import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PublicPage, PageHero, Eyebrow, DecorLines } from './public/PublicLayout';

const SolutionCard = ({ icon, title, description, features, navigate }) => (
    <div className="pub-surface pub-line border rounded-xl p-6 shadow-lg shadow-black/20 flex flex-col">
        <div className="pub-soft w-12 h-12 rounded-lg flex items-center justify-center mb-4">
            <i className={`${icon} text-xl`}></i>
        </div>
        <h3 className="pub-text text-xl font-bold mb-3">{title}</h3>
        <p className="pub-muted mb-4 text-sm leading-relaxed">{description}</p>
        <ul className="pub-muted text-sm space-y-2">
            {features.map((feature, index) => (
                <li key={index} className="flex items-start">
                    <i className="fas fa-check pub-icon mr-3 mt-1 flex-shrink-0"></i>
                    <span>{feature}</span>
                </li>
            ))}
        </ul>
        <div className="mt-auto pt-6">
            <button
                onClick={() => navigate('/contact')}
                className="w-full py-2.5 text-sm pub-btn font-semibold rounded-lg transition"
            >
                Learn more
            </button>
        </div>
    </div>
);

const SectionHeading = ({ eyebrow, title, children }) => (
    <div className="text-center mb-12">
        <Eyebrow center>{eyebrow}</Eyebrow>
        <h2 className="mt-5 text-3xl sm:text-5xl font-extrabold tracking-tight text-white">{title}</h2>
        {children && <p className="mt-4 text-lg sm:text-xl text-slate-200 max-w-3xl mx-auto">{children}</p>}
    </div>
);

const SolutionsPage = () => {
    const navigate = useNavigate();

    const pricingPlans = [
        {
            size: "Small Fleet",
            range: "1-10 trucks",
            price: "$39",
            features: ["Complete load management", "Driver mobile app", "Basic integrations", "Email support"],
        },
        {
            size: "Medium Fleet",
            range: "11-30 trucks",
            price: "$29",
            features: ["Everything in Small Fleet", "Advanced analytics", "API access & webhooks", "Priority support"],
            isPopular: true
        },
        {
            size: "Enterprise",
            range: "30+ trucks",
            price: "Custom",
            features: ["Everything in Medium Fleet", "Multi-Company/MC compliance", "Dedicated account manager", "24/7 phone support"],
        }
    ];

    const industrySolutions = [
        {
            icon: "fas fa-car-side",
            title: "Auto Hauling",
            description: "For vehicle transport: damage reports, VIN tracking and condition records.",
            features: ["Multi-vehicle load tracking", "Damage documentation", "VIN verification", "Condition photos"],
        },
        {
            icon: "fas fa-snowflake",
            title: "Reefer Transport",
            description: "Temperature logs, alerts and compliance reports for refrigerated loads.",
            features: ["Temperature logging", "Compliance alerts", "Cold chain documentation", "Maintenance tracking"],
        },
        {
            icon: "fas fa-boxes-stacked",
            title: "Dry Van & FTL",
            description: "For general freight: multi-stop routing, delivery tracking and cost analysis.",
            features: ["Load optimization", "Route planning & geofencing", "Delivery tracking & PODs", "Cost analysis & profitability"],
        }
    ];

    return (
        <PublicPage>
            <PageHero eyebrow="Solutions" title="Plans by fleet size." accent="Priced per truck.">
                Pick a plan, then the tools for the freight you haul.
            </PageHero>

            {/* Pricing */}
            <section className="pub-page py-16 sm:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <SectionHeading eyebrow="Pricing" title="Simple pricing">
                        You pay per truck, per month. The rate drops as your fleet grows.
                    </SectionHeading>

                    <div className="grid md:grid-cols-3 gap-8 md:gap-5">
                        {pricingPlans.map((plan, index) => (
                            <div
                                key={index}
                                className={`pub-surface relative rounded-2xl p-8 shadow-lg shadow-black/20 ${plan.isPopular ? 'border-2 pub-accent-border' : 'pub-line border'}`}
                            >
                                {plan.isPopular && (
                                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                                        <span className="pub-btn px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider">Recommended</span>
                                    </div>
                                )}
                                <div className="text-center">
                                    <h3 className="pub-text text-2xl sm:text-3xl font-bold mb-2">{plan.size}</h3>
                                    <p className="pub-muted mb-6">{plan.range}</p>
                                    <div className="mb-8">
                                        {plan.price === "Custom" ? (
                                            <span className="pub-link text-5xl font-extrabold">{plan.price}</span>
                                        ) : (
                                            <>
                                                <span className="pub-link text-5xl font-extrabold">{plan.price}</span>
                                                <span className="pub-muted text-lg">/truck/month</span>
                                            </>
                                        )}
                                    </div>
                                    <div className="space-y-4 mb-10 text-left">
                                        {plan.features.map((feature, fIndex) => (
                                            <div key={fIndex} className="flex items-center">
                                                <i className="fas fa-check pub-icon mr-3 flex-shrink-0"></i>
                                                <span className="pub-muted font-medium">{feature}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <button
                                        onClick={() => navigate('/contact')}
                                        className="w-full py-3 pub-btn rounded-lg font-semibold text-lg transition disabled:opacity-50"
                                    >
                                        {plan.price === "Custom" ? "Contact Sales" : "Choose Plan"}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Industry-specific */}
            <section className="pub-page pb-16 sm:pb-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <SectionHeading eyebrow="By freight type" title="Tools for what you haul">
                        Features built for the compliance and day-to-day work of each type of freight.
                    </SectionHeading>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {industrySolutions.map((solution, index) => (
                            <SolutionCard key={index} {...solution} navigate={navigate} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Implementation Process */}
            <section className="pub-page pb-16 sm:pb-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <SectionHeading eyebrow="Getting started" title="Set up in under 7 days">
                        We get your fleet onboarded in under 7 days, with support at every step.
                    </SectionHeading>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {[
                            { step: 1, title: "Discovery Call", description: "Book a demo. We match LoadMemo's features to how you work." },
                            { step: 2, title: "Data Migration", description: "We move your driver and vehicle data from your old system securely." },
                            { step: 3, title: "Training", description: "Role-based training for dispatchers, drivers and back-office staff." },
                            { step: 4, title: "Go Live & Support", description: "Go live with 24/7 dedicated support for the first 90 days." }
                        ].map(item => (
                            <div key={item.step} className="pub-surface pub-line border text-center rounded-xl p-6 shadow-lg shadow-black/20">
                                <div className="pub-soft w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <span className="text-2xl font-semibold">{item.step}</span>
                                </div>
                                <h3 className="pub-text text-xl font-semibold mb-2">{item.title}</h3>
                                <p className="pub-muted text-sm leading-relaxed">{item.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="relative overflow-hidden pub-band border-t border-white/10 py-16 sm:py-20">
                <DecorLines />
                <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white">See LoadMemo in action</h2>
                    <p className="mt-4 text-lg sm:text-xl text-slate-200">
                        Run your fleet from one system instead of several.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
                        <button
                            onClick={() => navigate('/contact')}
                            className="px-10 py-3.5 pub-btn rounded-lg font-semibold text-lg transition"
                        >
                            <i className="fas fa-calendar-days mr-3"></i>Request a demo
                        </button>
                        <button
                            onClick={() => navigate('/features')}
                            className="px-10 py-3.5 border-2 pub-accent-border text-white rounded-lg font-semibold text-lg pub-hover-tint transition"
                        >
                            <i className="fas fa-list-ul mr-3"></i>See all features
                        </button>
                    </div>
                </div>
            </section>
        </PublicPage>
    );
};

export default SolutionsPage;
